const SECONDS_PER_MINUTE = 60;

// The form stores a duration in whole minutes: a 3 min 10 s video counts as 4 minutes. A
// duration the browser cannot tell (0, NaN, Infinity on a fragmented file) gives undefined,
// so that the value already in the form is left alone instead of being replaced by a guess.
export const secondsToMinutes = (seconds: number): number | undefined => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return undefined;
  }
  return Math.ceil(seconds / SECONDS_PER_MINUTE);
};

const PROBE_TIMEOUT_MS = 10000;

// Reads the duration of a video file in the browser, without uploading it, by loading its
// metadata in a throwaway <video> element. Resolves with undefined when the browser cannot
// decode the file or does not answer in time (the duration stays editable by hand in the form).
export const readVideoDurationInMinutes = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const settle = (minutes: number | undefined) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      // release the decoder and the object url held by the detached element
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
      resolve(minutes);
    };

    const timer = setTimeout(() => settle(undefined), PROBE_TIMEOUT_MS);

    // Chrome defers a detached element asking for metadata only; asking for the whole file
    // starts the read at once, and a local object url costs nothing to read.
    video.preload = 'auto';
    video.muted = true;
    video.onloadedmetadata = () => settle(secondsToMinutes(video.duration));
    video.onerror = () => settle(undefined);
    video.src = objectUrl;
  });
